import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { User } from '../../../../model/user';
import { Tenant } from '../../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../../services/tenants.service';
import { noSpecialCharacters, noWhitespaces } from '../../../../shared/custom-validators';
import { validateEmail } from '../../../../common/validations';
import { Organization } from '../../../../model/organization';
import { AuthGuard } from '../../../../common/auth-guard.service';
import { LoggedUser } from '../../../../common/logged-user.service';
import { TimezoneService } from '../../../../services/timezone.service';

@Component({
  selector: 'app-users-form-info',
  templateUrl: './users-form-info.component.html',
  styleUrls: ['./users-form-info.component.scss']
})
export class UsersFormInfoComponent implements OnInit {

  @Input() mode: 'create' | 'edit';
  @Input() user: User;
  @Input() requiredUserActions: any[];
  @Output() userChange = new EventEmitter<User>();
  @Output() nextStep = new EventEmitter<void>();

  tenantSelected: boolean;
  tenants: Tenant[];
  organizations: Organization[];

  public timeZoneOptions: any[];
  public languageOptions: any[];
  public selectedLanguage: any;

  public userForm: FormGroup;
  public persistingChanges: any;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private loggedUser: LoggedUser,
              private tenantsService: TenantsService,
              private timezoneService: TimezoneService) {
    this.languageOptions = [
      {name: 'Español', code: 'es', langCode: 'es'},
      {name: 'English', code: 'gb', langCode: 'en'}
    ];
    this.timeZoneOptions = this.timezoneService.getAllTimezones();
  }

  ngOnInit(): void {
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    Promise.all([
      this.tenantsService.getTenantSummaries().toPromise(),
    ]).then(response => {
      this.tenants = response[0].tenants;
      if (this.user.language) {
        this.selectedLanguage = this.languageOptions.find(lang => lang.langCode === this.user.language);
      } else {
        this.user.language = this.languageOptions[0].langCode;
        this.selectedLanguage = this.languageOptions[0];
      }
      if (!this.authGuard.superAdminRequired()) {
        this.user.tenantUUID = this.tenants[0].uuid;
      }
      this.createForm(this.user);
    });
  }

  onNextStep(): void {
    if (this.userForm.valid) {
      this.nextStep.emit();
      this.userForm.get('language').setValue(this.selectedLanguage.langCode);
      this.userChange.emit(this.userForm.getRawValue());
    }
  }

  private createForm(user: User): void {
    this.userForm = this.formBuilder.group({
      tenantUUID: [user.tenantUUID, Validators.required],
      organizationUUID: [{value: user.organizationUUID, disabled: true}],
      uuid : [{value: user.uuid, disabled: true}, []],
      username: [user.username, [Validators.required, Validators.maxLength(255), noWhitespaces]],
      email: [user.email, [Validators.required, Validators.maxLength(255), validateEmail]],
      firstName: [user.firstName, [Validators.maxLength(255), noSpecialCharacters]],
      lastName: [user.lastName, [Validators.maxLength(255), noSpecialCharacters]],
      enabled: [user.enabled, []],
      emailVerified: [user.emailVerified, []],
      requieredActions: [user.requieredActions, []],
      language: [this.selectedLanguage, []],
      timeZone: [user.timeZone, []]
    });
    this.userForm.markAsPending();

    if (this.user.tenantUUID) {
      this.tenantSelected = true;
      this.loadOrganizations(this.user.tenantUUID);
    }

    if (this.mode === 'edit') {
      this.disableFixedFields();
    }

    if (!this.authGuard.superAdminRequired()) {
      this.userForm.get('tenantUUID').disable();
    }
  }

  private loadOrganizations(tenantUuid: string): void {
    Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
    ]).then(response => {
      this.organizations = [];
      this.organizations.push( new Organization() ); // EMPTY SELECTION
      response[0].organizations.forEach( organization => this.organizations.push(organization));
      if (!this.authGuard.superAdminRequired()) {
        this.loggedUser.userObservable.subscribe(loggedUser => {
          if (loggedUser.organizationUuid) {
            this.userForm.get('organizationUUID').setValue(loggedUser.organizations[0].uuid);
            this.userForm.get('organizationUUID').disable();
          } else {
            this.userForm.get('organizationUUID').enable();
          }
        });
      } else {
        this.userForm.get('organizationUUID').enable();
      }
    });
  }

  tenantChanged(event): void {
    if (event.value) {
      this.tenantSelected = true;
      this.loadOrganizations(event.value);
    }
  }

  private disableFixedFields(): void {
    this.userForm.get('username').disable();
    this.userForm.get('uuid').disable();
    this.userForm.get('tenantUUID').disable();
  }
}
